/** Player preferences persisted separately from save slots. */
import type { KeyBindings } from '../engine/input';
import type { TextStyle } from '../engine/font';
import type { Difficulty } from './types';

export type TextSpeed = 'slow' | 'normal' | 'fast' | 'instant';

/** How big item details (tooltips) are drawn: the original size, and three smaller ones. */
export type DetailSize = 'large' | 'medium' | 'small' | 'tiny';
export const DETAIL_SIZES: readonly DetailSize[] = ['large', 'medium', 'small', 'tiny'];
export const DETAIL_SIZE_INFO: Record<DetailSize, { label: string; frac: number; desc: string }> = {
  large: { label: 'Large', frac: 1, desc: 'The original size.' },
  medium: { label: 'Medium', frac: 0.8, desc: 'A step smaller.' },
  small: { label: 'Small', frac: 0.6, desc: 'Two steps smaller.' },
  tiny: { label: 'Tiny', frac: 0.4, desc: 'The smallest that stays readable; best on large, sharp screens.' },
};

export interface Settings {
  master: number;
  music: number;
  sfx: number;
  textSpeed: TextSpeed;
  autoAdvance: boolean;
  screenShake: number;
  damageNumbers: boolean;
  enemyHealthBars: boolean;
  autoLoot: boolean;
  showFps: boolean;
  tips: boolean;
  reduceFlashing: boolean;
  touchControls: 'auto' | 'on' | 'off';
  aimAssist: boolean;
  keys: Partial<KeyBindings>;
  /** Default difficulty for new games. */
  difficulty: Difficulty;
  /** The Developer section of Settings has been revealed (press confirm on the version line 7 times). */
  devUnlocked: boolean;
  /** Developer mode: adds the Dev tab (testing tools) to the pause menu. */
  devMode: boolean;
  /** How on-screen text is drawn (see engine/font TEXT_STYLES). */
  textStyle: TextStyle;
  /** How big item details are drawn. */
  detailSize: DetailSize;
}

export const DEFAULT_SETTINGS: Settings = {
  master: 0.8,
  music: 0.6,
  sfx: 0.8,
  textSpeed: 'normal',
  autoAdvance: false,
  screenShake: 1,
  damageNumbers: true,
  enemyHealthBars: true,
  autoLoot: true,
  showFps: false,
  tips: true,
  reduceFlashing: false,
  touchControls: 'auto',
  aimAssist: true,
  keys: {},
  difficulty: 'normal',
  devUnlocked: false,
  devMode: false,
  textStyle: 'crisp',
  detailSize: 'medium',
};

export const TEXT_SPEED_CPS: Record<TextSpeed, number> = {
  slow: 28,
  normal: 55,
  fast: 110,
  instant: 100000,
};

const KEY = 'aetherfall.settings';

export function loadSettings(storage: Pick<Storage, 'getItem'> = localStorage): Settings {
  try {
    const raw = storage.getItem(KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    return { ...DEFAULT_SETTINGS, ...(JSON.parse(raw) as Partial<Settings>) };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveSettings(s: Settings, storage: Pick<Storage, 'setItem'> = localStorage): void {
  try {
    storage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* storage full or unavailable (private mode) — settings just won't persist */
  }
}
